import React, { useState, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./Widget.css";
import icon from "./icon.png";

function Widget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showGreeting, setShowGreeting] = useState(true);
  const [showComplaintForm, setShowComplaintForm] = useState(false);
  const [showAskAQuestion, setShowAskAQuestion] = useState(false);
  const [widgetData, setWidgetData] = useState(null);

  // ✅ Fetch widget data from API
  useEffect(() => {
    const fetchWidget = async () => {
      try {
        const res = await fetch(
          "https://api-discloseai.essolutions.ai/api/v1/admin/get-widget",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              org_id: 3,
            }),
          }
        );
        const data = await res.json();
        setWidgetData(data);
      } catch (error) {
        console.error("Widget fetch error:", error);
      }
    };
    fetchWidget();
  }, []);

  const toggleWidget = () => {
    setIsOpen((prev) => !prev);
  };

  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  /* ---------------- CHAT BODY ---------------- */
  const renderChatBody = () => {
    // Greeting screen
    if (showGreeting && !showAskAQuestion && !showComplaintForm) {
      return (
        <div className="greeting-container">
          <div
            className="option-card cursor-pointer"
            onClick={() => {
              setShowGreeting(false);
              setShowAskAQuestion(true);
            }}
          >
            <h6>{widgetData?.ask_question_title || "Ask a Question"}</h6>
            <p>
              {widgetData?.ask_question_desc ||
                "Find answers about this project, sustainability, and disclosures."}
            </p>
          </div>

          {widgetData?.opt_complaint === false && (
            <div
              className="option-card cursor-pointer"
              onClick={() => {
                setShowGreeting(false);
                setShowComplaintForm(true);
              }}
            >
              <h6>{"Feedback"}</h6>
              <p>
                {widgetData?.complaint_desc ||
                  "Report concerns securely. You may remain anonymous."}
              </p>
            </div>
          )}
        </div>
      );
    }

    // Ask a Question
    if (showAskAQuestion) {
      return (
        <AskAQuestion
          widgetData={widgetData}
          setShowComplaintForm={setShowComplaintForm}
          setShowAskAQuestion={setShowAskAQuestion}
          setShowGreeting={setShowGreeting}
        />
      );
    }

    // Complaint Form
    return (
      <ComplaintForm
        widgetData={widgetData}
        goBack={() => {
          setShowComplaintForm(false);
          setShowGreeting(true);
        }}
      />
    );
  };

  return (
    <div className="homepage">
      <div
        className={`chat-widget ${isOpen ? "open" : "closed"} ${
          isExpanded ? "expanded" : ""
        }`}
        style={{
          backgroundColor: widgetData?.primary_color || "#0d6efd",
          color: widgetData?.secondary_color || "#fff",
          fontFamily: widgetData?.font_style || "inherit",
        }}
      >
        <div className="chat-header">
          <div className="header-left">
            {showGreeting && (
              <img
                className="avatar-small"
                src={widgetData?.logo_icon || icon}
                alt="assistant"
              />
            )}
          </div>

          <div className="header-icons">
            <button className="icon-btn" onClick={toggleExpand}>
              <i
                className={`bi ${
                  isExpanded
                    ? "bi-arrows-angle-contract"
                    : "bi-arrows-angle-expand"
                }`}
              ></i>
            </button>

            <button className="icon-btn" onClick={() => setIsOpen(false)}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        <div className="chat-body">{renderChatBody()}</div>
      </div>

      {/* Floating Launcher */}
      <div className="chat-launcher">
        <button
          className="launcher-btn"
          onClick={toggleWidget}
          style={{
            backgroundColor: widgetData?.primary_color || "#0d6efd",
          }}
        >
          <i
            className={`bi ${
              isOpen ? "bi-chevron-down" : "bi-chevron-up"
            } fs-4`}
          ></i>
        </button>
      </div>
    </div>
  );
}

function QuestionSelector({ widgetData, onSelect, goBack }) {
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!search.trim()) {
      setOptions([]);
      return;
    }

    const fetchOptions = async () => {
      try {
        setLoading(true);

        const res = await fetch(
          "https://api-discloseai.essolutions.ai/api/v1/admin/fetch-namespaces"
        );

        const json = await res.json();

        // ✅ YOUR EXACT RESPONSE HANDLING
        const namespaces = Array.isArray(json?.all_namespaces)
          ? json.all_namespaces
          : [];

        // Normalize into dropdown format
        const list = namespaces.map((name) => ({
          label: name,
        }));

        // 🔍 Client-side filter
        const filtered = list.filter((opt) =>
          opt.label.toLowerCase().includes(search.toLowerCase())
        );

        setOptions(filtered);
      } catch (error) {
        console.error("Namespace fetch error:", error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    const delay = setTimeout(fetchOptions, 400);
    return () => clearTimeout(delay);
  }, [search]);

  return (
    <div className="h-100 d-flex flex-column justify-content-between text-start">
      <div>
        <h5 className="mb-2">
          {widgetData?.question_prompt || "Select a topic"}
        </h5>

        <p className="small text-muted mb-3">
          Start typing to search and select a topic.
        </p>

        {/* Input */}
        <input
          type="text"
          className="form-control mb-2"
          placeholder="Type to search..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setSelected(null);
          }}
        />

        {/* Dropdown */}
        <div className="border rounded bg-white">
          {loading && <div className="p-2 small text-muted">Loading...</div>}

          {!loading &&
            Array.isArray(options) &&
            options.map((opt, index) => (
              <div
                key={index}
                className={`p-2 cursor-pointer ${
                  selected?.label === opt.label ? "bg-light" : ""
                }`}
                onClick={() => {
                  setSelected(opt);
                  setSearch(opt.label);
                  setOptions([]);
                }}
              >
                {opt.label}
              </div>
            ))}

          {!loading && search && options.length === 0 && (
            <div className="p-2 small text-muted">No results</div>
          )}
        </div>
      </div>

      {/* Buttons */}
      <div className="d-flex justify-content-between mt-4">
        <button
          className="btn btn-outline-light rounded-pill px-4"
          onClick={goBack}
        >
          ← Back
        </button>

        <button
          className="btn btn-primary rounded-pill px-4"
          disabled={!selected}
          onClick={() => onSelect(selected)}
        >
          Continue
        </button>
      </div>
    </div>
  );
}

function AskAQuestion({
  widgetData,
  setShowComplaintForm,
  setShowAskAQuestion,
  setShowGreeting,
}) {
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: widgetData?.question_prompt || "What do you want to ask today?",
      hint:
        widgetData?.question_hint ||
        "You can ask about project details, environmental and social measures, or sustainability reports.",
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speakingIndex, setSpeakingIndex] = useState(null);

  const recognitionRef = useRef(null);

  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();

    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.onerror = () => {
      setIsListening(false);
    };

    recognition.onresult = (event) => {
      let transcript = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }

      setQuestion(transcript);
    };

    recognitionRef.current = recognition;
  }, []);

  const startListening = () => {
    recognitionRef.current?.start();
  };

  const stopListening = () => {
    recognitionRef.current?.stop();
  };

  const speakText = (text, index) => {
    if (!window.speechSynthesis) return;

    // Stop if the same message is already being spoken
    if (window.speechSynthesis.speaking && speakingIndex === index) {
      window.speechSynthesis.cancel();
      setSpeakingIndex(null);
      return;
    }

    // Stop any previous speech
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);

    utterance.lang = "en-US";
    utterance.rate = 1;
    utterance.pitch = 1;

    utterance.onstart = () => setSpeakingIndex(index);
    utterance.onend = () => setSpeakingIndex(null);
    utterance.onerror = () => setSpeakingIndex(null);

    window.speechSynthesis.speak(utterance);
  };

  const goBack = () => {
    setShowAskAQuestion(false);
    setShowGreeting(true);
  };

  const handleSend = async () => {
    if (!question.trim()) return;

    const userMessage = { sender: "user", text: question };
    setMessages((prev) => [...prev, userMessage]);
    setQuestion("");
    setLoading(true);

    try {
      const response = await fetch(
        "https://api-discloseai.essolutions.ai/api/v1/ai/merchant/query",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            query: question,
            top_k: 5,
            include_sources: true,
            project: "Sierra_Leone",
            email: "ildiko@essolutions.ai",
          }),
        }
      );

      const data = await response.json();

      // You can modify this depending on the API's response structure
      const botReply =
        data?.answer || data?.message || "Sorry, I didn’t understand that.";
      const botMessage = { sender: "bot", text: botReply };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error("API Error:", error);
      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: "Something went wrong. Please try again." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSend();
  };

  return (
    <div className="ask-question d-flex flex-column h-100 text-start">
      {/* 🔙 BACK BUTTON */}
      <div className="mb-2">
        <button
          className="btn btn-outline-light btn-sm rounded-pill"
          onClick={goBack}
        >
          ← Back
        </button>
      </div>
      <div className="chat-messages flex-grow-1 overflow-auto mb-3">
        {messages.map((msg, index) =>
          msg.sender === "bot" ? (
            <div key={index} className="d-flex mb-3">
              <div className="avatar-sm me-2">
                <img
                  src={widgetData?.chat_icon || icon}
                  alt="bot"
                  className="rounded-circle"
                  width="38"
                />
              </div>
              <div
                className="rounded-3 py-2 px-3 small"
                style={{ maxWidth: "85%" }}
              >
                <p className="mb-2">{msg.text}</p>
                {msg.hint && (
                  <div className="text-secondary small">{msg.hint}</div>
                )}
                <div className="d-flex justify-content-end">
                  <button
                    className="chat-speech-btn"
                    onClick={() => speakText(msg.text, index)}
                  >
                    {speakingIndex === index ? (
                      <i className="bi bi-stop-fill"></i>
                    ) : (
                      <i className="bi bi-volume-up-fill"></i>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div key={index} className="d-flex justify-content-end mb-3">
              <div
                className="text-white rounded-3 py-2 px-3 small"
                style={{
                  maxWidth: "80%",
                  backgroundColor: widgetData?.secondary_color || "#007bff",
                }}
              >
                {msg.text}
              </div>
            </div>
          )
        )}

        {loading && (
          <div className="d-flex mb-3">
            <div className="avatar-sm me-2">
              <img
                src={widgetData?.chat_icon || icon}
                alt="bot"
                className="rounded-circle"
                width="38"
              />
            </div>
            <div className="rounded-3 py-2 px-3 small text-secondary">
              Typing...
            </div>
          </div>
        )}

        {widgetData?.opt_complaint === false && (
          <div className="d-flex justify-content-center mt-auto">
            <button
              className="btn btn-outline-light rounded-pill px-4"
              onClick={() => {
                setShowComplaintForm(true);
                setShowAskAQuestion(false);
              }}
            >
              Feedback
            </button>
          </div>
        )}
      </div>

      <div className="chat-input d-flex align-items-center border-top pt-2">
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask anything..."
          className="form-control border-0 bg-transparent text-white me-2"
        />
                <button
          className="btn btn-outline-light rounded-circle me-2"
          onClick={isListening ? stopListening : startListening}
        >
          {isListening ? (
            <i className="bi bi-mic-fill text-danger"></i>
          ) : (
            <i className="bi bi-mic"></i>
          )}
        </button>
      </div>
    </div>
  );
}

/* Complaint Form Component */
function ComplaintForm({ goBack }) {
  const [identity, setIdentity] = useState("anonymous");
  const [toldBefore, setToldBefore] = useState("no");
  const [hasComments, setHasComments] = useState("no");
  const [confirmEmail, setConfirmEmail] = useState("no");

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    details: "",
    channel: "",
    district: ""
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  // handle change for text inputs and selects
  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const payload = {
      name: formData.fullName,
      email: formData.email,
      channel:formData.channel,
      district:formData.district,
      phone: formData.phone,
      feedback: formData.details,
      confirmation_mail: confirmEmail === "yes" ? "true" : "false",
      topic: "technical",
      status: "In Progress",
      priority: "low",
      assigned_to: "Himanshu",
      prior_reported: true,
      prior_details: "Details",
      project_id: 2,
    };

    try {
      const response = await fetch(
        "https://api-discloseai.essolutions.ai/api/v1/complaint/create-complaint-sierra",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error("Failed to submit complaint");

      setSubmitted(true);
    } catch (err) {
      console.error("Submission error:", err);
      setError("Something went wrong. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ Success page
  if (submitted) {
    return (
      <div className="text-center py-5">
        <h4 className="text-success mb-3">
          <i className="bi bi-check-circle-fill me-2"></i>
          Complaint Submitted Successfully!
        </h4>
        <p className="text-secondary mb-4">
          Thank you for sharing your concern. Our team will review it shortly.
        </p>
        <button
          className="btn btn-outline-light px-4 rounded-pill"
          onClick={goBack}
        >
          Back to Chat
        </button>
      </div>
    );
  }

  return (
    <form className="complaint-form text-start" onSubmit={handleSubmit}>
      <h5 className="form-title">Submit a Feedback</h5>

      <>
        <input
          type="text"
          name="fullName"
          value={formData.fullName}
          onChange={handleChange}
          placeholder="Full Name"
          className="form-control mb-3 rounded-input"
          required
        />
        <input
          type="text"
          name="channel"
          value={formData.channel}
          onChange={handleChange}
          placeholder="Channel"
          className="form-control mb-3 rounded-input"
          required
        />
        <input
          type="text"
          name="district"
          value={formData.district}
          onChange={handleChange}
          placeholder="District"
          className="form-control mb-3 rounded-input"
          required
        />
        <div className="d-flex flex-column flex-sm-row gap-2 mb-3">
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Email"
            className="form-control rounded-input"
          />
          <input
            type="text"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Contact No."
            className="form-control rounded-input"
          />
        </div>
      </>

      {/* Complaint Details */}
      <label className="fw-semibold mb-1">Feedback Details</label>
      <textarea
        name="details"
        value={formData.details}
        onChange={handleChange}
        placeholder="Describe your Feedback in detail..."
        className="form-control mb-3 rounded-input"
        rows={4}
        required
      ></textarea>

      {/* Confirmation Email Section */}
      <label className="fw-semibold mb-1">
        Do you want to receive a confirmation email?
      </label>
      <div className="d-flex flex-wrap gap-3 mb-3">
        <label className="radio-label">
          <input
            type="radio"
            name="confirmEmail"
            value="yes"
            checked={confirmEmail === "yes"}
            onChange={() => setConfirmEmail("yes")}
          />{" "}
          Yes
        </label>
        <label className="radio-label">
          <input
            type="radio"
            name="confirmEmail"
            value="no"
            checked={confirmEmail === "no"}
            onChange={() => setConfirmEmail("no")}
          />{" "}
          No
        </label>
      </div>
      {/* Error Message */}
      {error && (
        <div className="alert alert-danger py-2 small mb-3">{error}</div>
      )}

      {/* Buttons */}
      <div className="d-flex justify-content-between mt-4">
        <button
          type="button"
          className="btn btn-outline-light px-4 rounded-pill"
          onClick={goBack}
        >
          ← Back
        </button>
        <button
          type="submit"
          className="btn btn-primary px-4 rounded-pill"
          disabled={submitting}
        >
          {submitting ? "Submitting..." : "Submit"}
        </button>
      </div>
    </form>
  );
}

export default Widget;
